import React, { useState } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Building2, ChevronDown, MapPin, Check, Globe } from "lucide-react";

export default function BranchSelector() {
    const { branches, hasBranches, selectedBranchId, selectedBranch, selectBranch } = useBranch();
    const [open, setOpen] = useState(false);

    if (!hasBranches) return null;

    return (
        <div className="sticky top-[57px] z-20 border-b bg-gradient-to-r from-zinc-50 to-white" data-testid="branch-bar">
            <div className="px-4 lg:px-6 py-2.5 flex items-center gap-3">
                <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider hidden sm:block">Sucursal</span>

                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <button
                            data-testid="branch-selector-trigger"
                            className="flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-xl border border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm transition-all text-sm min-w-[220px] max-w-[400px] group"
                        >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                selectedBranch
                                    ? "bg-[#CC0000]/10 text-[#CC0000]"
                                    : "bg-zinc-100 text-zinc-500"
                            }`}>
                                {selectedBranch ? <MapPin className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                            </div>
                            <div className="text-left min-w-0 flex-1">
                                <div className="font-semibold text-zinc-800 truncate">
                                    {selectedBranch ? selectedBranch.name : "Todas las sucursales"}
                                </div>
                                <div className="text-[11px] text-zinc-400 truncate">
                                    {selectedBranch
                                        ? (selectedBranch.address || selectedBranch.city || "")
                                        : `${branches.length} ubicaciones`}
                                </div>
                            </div>
                            <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0 group-hover:text-zinc-600 transition-colors" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[340px] p-0" align="start">
                        <Command>
                            <CommandInput
                                placeholder="Buscar sucursal..."
                                data-testid="branch-search-input"
                            />
                            <CommandList className="max-h-[300px]">
                                <CommandEmpty>No se encontró sucursal</CommandEmpty>
                                <CommandGroup>
                                    <CommandItem
                                        value="__all__"
                                        onSelect={() => { selectBranch(null); setOpen(false); }}
                                        data-testid="branch-option-all"
                                        className="flex items-center gap-3 py-2.5"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                                            <Globe className="w-4 h-4 text-zinc-500" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-medium">Todas las sucursales</div>
                                            <div className="text-xs text-zinc-400">{branches.length} ubicaciones</div>
                                        </div>
                                        {!selectedBranchId && <Check className="w-4 h-4 text-[#CC0000]" />}
                                    </CommandItem>

                                    {branches.map((b) => (
                                        <CommandItem
                                            key={b.id}
                                            value={b.name}
                                            onSelect={() => { selectBranch(b.id); setOpen(false); }}
                                            data-testid={`branch-option-${b.id}`}
                                            className="flex items-center gap-3 py-2.5"
                                        >
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                                selectedBranchId === b.id
                                                    ? "bg-[#CC0000]/10 text-[#CC0000]"
                                                    : "bg-zinc-50 text-zinc-400"
                                            }`}>
                                                <MapPin className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium text-sm">{b.name}</div>
                                                <div className="truncate text-xs text-zinc-400">
                                                    {[b.city, b.phone].filter(Boolean).join(" · ")}
                                                </div>
                                            </div>
                                            {selectedBranchId === b.id && <Check className="w-4 h-4 text-[#CC0000] shrink-0" />}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                {selectedBranch && selectedBranch.city && (
                    <span className="text-xs text-zinc-400 hidden md:block">
                        {selectedBranch.city}
                        {selectedBranch.phone && ` · ${selectedBranch.phone}`}
                    </span>
                )}
            </div>
        </div>
    );
}
